import { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import {
  Home, BookOpen, ClipboardCheck, Calculator, PoundSterling,
  TrendingUp, LogOut, ChevronLeft,
  ChevronRight, ChevronDown, Menu, X, Pin, PinOff,
  GitPullRequestArrow, Zap, Wrench, MailOpen, PenLine,
  PenTool, FolderInput, Shield, ShieldAlert, LayoutDashboard, Camera,
  FileText, CheckCircle2, Scale, Users, SendHorizonal, Mail, Bot, Clock, ListChecks, Radar, FolderOpen, Banknote, Receipt, FilePlus, Workflow, Landmark
} from "lucide-react";
import axios from "axios";
import { API } from "../config/api";
import { canFixClient, ADMIN_EMAILS } from "../config/staffConfig";
import { getUserRole, ROLES } from "../config/roleConfig";

// BM-H3509: matches compliance/dashboard_router._require_mlco_or_colp.
const _mlcoOrColp = (userMeta) => !!(userMeta?.is_mlco || userMeta?.is_colp);
// BM-H3980: policy owners (the MLCO and COLP) and the MLRO, who may approve the AML policy.
const _policyOwner = (userMeta) => _mlcoOrColp(userMeta) || !!userMeta?.is_mlro;
// Directors and Accounts (May): the Finance pages only they act on.
const _directorOrAccounts = (userMeta) => [ROLES.DIRECTOR, ROLES.ACCOUNTS].includes(getUserRole(userMeta?.email));

// 26 Oct 2026 system-wide refresh (the Bonsai Agents look): each group has
// its own hue, so its icons are easy to find at a glance. Light 400 shades,
// readable on navy, and never green, amber or red, which mean status.
const GROUP_HUES = {
  Dashboard: ["#818cf8", "#a78bfa"],
  "New Matter": ["#a78bfa", "#f472b6"],
  "Pipeline Tracker": ["#60a5fa", "#818cf8"],
  "My Time": ["#38bdf8", "#60a5fa"],
  "Resi Pipeline": ["#f472b6", "#c084fc"],
  Actions: ["#e879f9", "#a78bfa"],
  "Bonsai Agents": ["#c084fc", "#f472b6"],
  "Post Hub": ["#22d3ee", "#60a5fa"],
  "File Snap": ["#38bdf8", "#c084fc"],
  Finance: ["#a78bfa", "#60a5fa"],
  Tools: ["#22d3ee", "#818cf8"],
  Compliance: ["#818cf8", "#e879f9"],
  "Compliance officer": ["#c084fc", "#818cf8"],
};
const DEFAULT_HUE = ["#818cf8", "#a78bfa"];
const hueVars = (label) => { const [c1, c2] = GROUP_HUES[label] || DEFAULT_HUE; return { "--c1": c1, "--c2": c2 }; };

const NAV_GROUPS = [
  {
    label: "Dashboard",
    items: [
      { path: "/dashboard", label: "Dashboard", icon: Home, tooltip: "Home & overview" },
    ],
  },
  {
    label: "New Matter",
    items: [
      { path: "/quotes", label: "Quotes", icon: FileText, tooltip: "Quote tracker — draft, send and track fee quotes" },
      { path: "/conflict-search", label: "New Matter", icon: Shield, tooltip: "Conflict Search — the single new-matter funnel" },
    ],
  },
  {
    label: "Pipeline Tracker",
    items: [
      { path: "/pipeline", label: "Pipeline Tracker", icon: GitPullRequestArrow, tooltip: "Full matter pipeline" },
      // BM-H3465 — open matters were reachable only from the pipeline
      // banner link; staff could not find them from the nav.
      { path: "/matters", label: "Open Matters", icon: FolderOpen, tooltip: "Matters that are open (CCL signed)" },
    ],
  },
  {
    // time-recording-foundations Ticket 5 — every fee earner's daily tool,
    // so it gets first-class top-level placement rather than living under
    // Finance (which most fee earners don't otherwise open).
    label: "My Time",
    items: [
      { path: "/my-time", label: "My Time", icon: Clock, tooltip: "Log and manage your own time entries" },
      // BM-H3620: team records expenses/disbursements as they go.
      { path: "/add-expense", label: "Add Expense", icon: Receipt, tooltip: "Record an expense or disbursement against a matter" },
    ],
  },
  {
    label: "Resi Pipeline",
    showDots: true,
    // BM-H3648: folds away behind its heading (collapsed by default) so the
    // nav is shorter for the many staff who never open these pages. Each
    // person's choice is kept in their browser; see groupOpenKey below.
    collapsible: true,
    items: [
      { path: "/pre-exchange-checklist", label: "Pre-Exchange", icon: ClipboardCheck, tooltip: "Resi pre-exchange tasks" },
      { path: "/pre-completion-checklist", label: "Pre-Completion", icon: ClipboardCheck, tooltip: "Resi pre-completion tasks" },
      // BM-H4191: remortgage and transfer of equity have no exchange; one checklist to completion.
      { path: "/refinance-completion-checklist", label: "Remortgage / TOE", icon: ClipboardCheck, tooltip: "Remortgage and transfer of equity completion checklist (individual or company)" },
      { path: "/completion-statement", label: "Completion Stmt", icon: Calculator, tooltip: "Generate completion statement" },
      { path: "/completions-book", label: "Completions Book", icon: BookOpen, tooltip: "Track completions" },
      { path: "/post-completion", label: "Post-Completion", icon: ClipboardCheck, tooltip: "Post-completion tasks" },
    ],
  },
  {
    label: "Actions",
    items: [
      { path: "/actions", label: "Actions", icon: Zap, tooltip: "Action requests & submissions" },
      // BM-H5047: trainee work waiting for review, per matter.
      { path: "/supervision", label: "Supervision board", icon: ClipboardCheck, tooltip: "Trainee work: drafting, waiting for review, sent back, approved, sent" },
    ],
  },
  {
    // BM-H620 — the Reviews page, renamed Bonsai Agents. Gated on the live
    // backend flag (GET /api/tenant/features), not a build-time env var, so
    // it can be switched on for everyone with one env var change once
    // Vanessa has tested it, with no frontend rebuild needed.
    label: "Bonsai Agents",
    visible: (userMeta) => !!userMeta?.reviews_page_enabled,
    items: [
      { path: "/bonsai-agents", label: "Bonsai Agents", icon: Bot, tooltip: "Start, watch and collect a specialist agent review" },
    ],
  },
  {
    label: "Post Hub",
    items: [
      { path: "/post-hub", label: "Post Hub", icon: MailOpen, tooltip: "Incoming & outgoing post" },
    ],
  },
  {
    // Global File Snap hub — cross-matter view of every file snap. Gated by the
    // build-time FILE_SNAP_HUB_ENABLED flag (default off), mirroring the backend
    // FILE_SNAP_HUB_ENABLED gate and the ComplianceMinder visible() pattern.
    label: "File Snap",
    visible: () => process.env.REACT_APP_FILE_SNAP_HUB_ENABLED === "true",
    items: [
      { path: "/file-snap", label: "File Snap", icon: Camera, tooltip: "All file snaps across your matters" },
    ],
  },
  {
    label: "Finance",
    items: [
      // BM-H4390: May asked for invoicing under Finance. New Invoice is the
      // existing invoice request form (it leads into the native invoice
      // editor); Invoices lists every client invoice, draft to void.
      { path: "/invoice-request", label: "New Invoice", icon: FilePlus, tooltip: "Start an invoice for a matter", visible: _directorOrAccounts },
      { path: "/finance/invoices", label: "Invoices", icon: FileText, tooltip: "Every client invoice: drafts, issued and void", visible: _directorOrAccounts },
      { path: "/invoice-tracker", label: "Invoice Tracker", icon: TrendingUp, tooltip: "Track invoices" },
      { path: "/credit-control", label: "Credit Control", icon: PoundSterling, tooltip: "Credit control dashboard" },
      // BM-H3488: May asked for it here. Directors and Accounts only, the same
      // gate as the page and its list endpoint, so nobody is shown a link
      // only to be refused.
      {
        path: "/finance/payment-tracker",
        label: "Payment Tracker",
        icon: Banknote,
        tooltip: "Track payment requests",
        visible: _directorOrAccounts,
      },
      { path: "/trust-accounts-report", label: "Trust Accounts", icon: Scale, tooltip: "Client & office positions across matters" },
      // BM-H4699: client ledgers, cash book, bank accounts, opening balances.
      { path: "/client-account", label: "Client Account", icon: Landmark, tooltip: "Client ledgers, cash book and bank accounts", visible: _directorOrAccounts },
    ],
  },
  {
    label: "Tools",
    items: [
      { path: "/bonsai-pen", label: "Bonsai Pen", icon: PenLine, tooltip: "Draft documents with AI" },
      { path: "/signatures", label: "Signatures", icon: PenTool, tooltip: "Manage e-signatures" },
      { path: "/client-doc-request", label: "Client Doc Request", icon: FolderInput, tooltip: "Ask for docs and send SoF & TA forms" },
      { path: "/guides", label: "Guides", icon: BookOpen, tooltip: "Staff guides library" },
      { path: "/admin/ref-fixer", label: "Ref Fixer", icon: Wrench, tooltip: "Fix matter references" },
      // BM-H284. Directors, the Executive Assistant and (24 Sep 2026) client
      // care only, so it is
      // hidden from everyone else rather than shown and then refused.
      // The backend gate is the real one; this only keeps the nav honest.
      {
        path: "/admin/client-fixer",
        label: "Client Fixer",
        icon: Users,
        tooltip: "Change who the client on a matter is",
        visible: (userMeta) => canFixClient(userMeta?.email),
      },
      // BM-H452: the client register discovery page. Without this link the
      // page was reachable only by typing its address. Shown to directors and
      // admins, the same people the backend lets see every practice area
      // when they have no explicit grant row (client_register_routes).
      {
        path: "/client-register-discovery",
        label: "Client Registers",
        icon: ListChecks,
        tooltip: "Find which matters have a FORF, OB and completion documents, by practice area",
        visible: (userMeta) => !!(userMeta?.is_director || ADMIN_EMAILS.has(userMeta?.email)),
      },
      { path: "/ob-email-log", label: "OB Email Log", icon: SendHorizonal, tooltip: "Every onboarding invite sent, with delivery status and any send error" },
      // BM-H548: firm-wide outbound history. The portal sends from screens all
      // over the app and, before this, two thirds of those sends were visible
      // nowhere at all.
      { path: "/sent-email-log", label: "Sent Email Log", icon: Mail, tooltip: "Every email sent by the portal, with sender, recipient and outcome" },
      { path: "/operator", label: "Operator", icon: Shield, tooltip: "Self-serve remediation console" },
      // BM-H4600 (23 Oct 2026): Admin Tools (Connect QuickBooks, the sync
      // buttons) had no menu entry, so it was reachable only by its address.
      { path: "/admin/tools", label: "Admin Tools", icon: Wrench, tooltip: "Directors: QuickBooks connection, syncs and other admin tools", visible: (userMeta) => !!userMeta?.is_director },
    ],
  },
  {
    // Compliance surface visible to every staff member — read policies,
    // acknowledge them, raise notification forms (breaches, complaints,
    // gifts, data incidents, conflicts, economic-crime incidents).
    label: "Compliance",
    items: [
      {
        path: "/policy/my",
        label: "My Policies",
        icon: CheckCircle2,
        tooltip: "Read and acknowledge firm policies",
      },
      {
        path: "/policy/library",
        label: "Policy Library",
        icon: BookOpen,
        tooltip: "Browse firm policies (read-only)",
      },
      // BM-H3370: the Complaint Notification Form, for every member of staff.
      { path: "/complaints/new", label: "Report a Complaint", icon: FileText, tooltip: "Report any complaint to the Complaints Manager (Complaint Management Policy §6)" },
      // BM-H3613: the firm's registers, held in CaseMinder, for every member of staff.
      // BM-H3630: everyone's training, with the annual AML course.
      { path: "/training", label: "Training", icon: BookOpen, tooltip: "Your training and the annual AML assessment" },
      // BM-H4201: each person's ongoing competence log (SRA reflective record; CLC 8 activities from 1 Nov 2026).
      { path: "/compliance/mvp/competence", label: "My Competence Log", icon: BookOpen, tooltip: "Log what you have learned and how you have applied it", visible: () => process.env.REACT_APP_COMPLIANCEMINDER_ENABLED === "true" },
      { path: "/compliance/registers", label: "Registers", icon: ClipboardCheck, tooltip: "Record undertakings, gifts, declined instructions, breaches, training and the firm's other registers" },
      {
        path: "/policy/forms",
        label: "Notification Forms",
        icon: FileText,
        tooltip: "Raise a compliance notification: breach, complaint, gift, conflict, incident",
      },
    ],
  },
  {
    // Compliance-officer cockpit — MLCO / COLP / MD / admin only.
    // BM-H3509: items whose server gate is MLCO or COLP only carry
    // visible: _mlcoOrColp, so a director who is neither is not shown a
    // link that ends in an error page.
    // Registers hold cross-staff PII so they stay behind this gate.
    label: "Compliance officer",
    visible: (userMeta) => !!(userMeta?.is_mlco || userMeta?.is_colp || userMeta?.is_director),
    items: [
      { path: "/compliance/mlco-inbox", label: "MLCO Inbox (queue)", icon: ShieldAlert, tooltip: "MLCO red-flag queue" },
      { visible: (m) => _mlcoOrColp(m) || !!m?.is_director, path: "/compliance/dashboard", label: "Compliance Dashboard", icon: LayoutDashboard, tooltip: "MLCO + COLP compliance overview, including AML status, attestations, breaches and control tests" },
      { path: "/compliance/complaints", label: "Complaints", icon: ClipboardCheck, tooltip: "Complaints and their deadlines: acknowledge in 72 hours, respond in 21 days, resolve in 8 weeks" },
      { visible: _mlcoOrColp, path: "/compliance/horizon-scan", label: "Horizon Scan", icon: Radar, tooltip: "Regulatory and legal updates: run a scan, set the weekly schedule, triage what is new" },
      { visible: _policyOwner, path: "/compliance/change-pack", label: "Change Pack", icon: FileText, tooltip: "Policy changes from the horizon scan: draft, approve and publish them together on one date" },
      { visible: (m) => _policyOwner(m) || !!m?.is_director, path: "/compliance/process-changes", label: "Process Changes", icon: Workflow, tooltip: "What CaseMinder must change after a policy change: Claude drafts it, the owner checks it, a director signs it off" },
      {
        path: "/policy/registers",
        label: "Registers",
        icon: ClipboardCheck,
        tooltip: "Mandated compliance registers (Brief 26a)",
      },
      {
        path: "/policy/inbox/mlco",
        label: "Policy CR Inbox",
        icon: ClipboardCheck,
        tooltip: "Compliance change-request inbox (Brief 26a)",
      },
    ],
  },
];

const FOOTER_ITEMS = [];

// BM-H3648: per-browser memory of whether a collapsible group is open.
export const groupOpenKey = (label) => `sidebar_group_open:${label}`;

const readGroupOpen = (label) => {
  try {
    return localStorage.getItem(groupOpenKey(label)) === "true";
  } catch {
    return false;
  }
};

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "true");
  const [pinned, setPinned] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMeta, setUserMeta] = useState(null);
  const [openGroups, setOpenGroups] = useState(() => Object.fromEntries(
    NAV_GROUPS.filter((g) => g.collapsible).map((g) => [g.label, readGroupOpen(g.label)]),
  ));
  const location = useLocation();
  const { logout } = useAuth();
  const overlayRef = useRef(null);

  // Brief 14 PR2 — fetch role meta once on mount so the Compliance group's
  // visible() predicate has data to evaluate. Soft-fail to empty object on
  // error; the group simply stays hidden.
  useEffect(() => {
    let cancelled = false;
    const email =
      sessionStorage.getItem("bonsai_user_id") ||
      localStorage.getItem("bonsai_user_id") ||
      "";
    if (!email) { setUserMeta({}); return; }
    (async () => {
      let roleMeta = {};
      try {
        const res = await axios.get(`${API}/user-role`, {
          headers: { "X-Bonsai-User-Id": email },
        });
        roleMeta = res.data || {};
      } catch {
        // BM-H284: keep the identity even when /user-role fails, so an
        // item gated on the caller's email is judged on the identity we
        // already hold rather than silently disappearing.
      }
      // BM-H620 — Bonsai Agents stays hidden (safe default off) if this
      // read fails for any reason, same contract as the SoF feature flag.
      let reviewsPageEnabled = false;
      try {
        const feat = await axios.get(`${API}/tenant/features`);
        reviewsPageEnabled = !!feat.data?.reviews_page_enabled;
      } catch {
        // stays hidden
      }
      if (!cancelled) setUserMeta({ email, ...roleMeta, reviews_page_enabled: reviewsPageEnabled });
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    localStorage.setItem("sidebar_collapsed", String(collapsed));
  }, [collapsed]);

  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/" || location.pathname === "/dashboard";
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const isCollapsed = collapsed && !pinned;

  const toggleGroup = (label) => {
    setOpenGroups((prev) => {
      const next = !prev[label];
      try {
        localStorage.setItem(groupOpenKey(label), String(next));
      } catch {
        // the choice simply is not remembered in this browser
      }
      return { ...prev, [label]: next };
    });
  };

  const sidebarContent = (
    <div className="flex flex-col h-full" data-testid="sidebar-nav">
      {/* Logo / Brand */}
      <div className={`flex items-center h-14 px-3 border-b border-slate-700/50 shrink-0 ${isCollapsed ? "justify-center" : "gap-2.5"}`}>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ring-1 ring-white/20 shadow-md shadow-fuchsia-900/40"
          style={{ background: "linear-gradient(135deg, #6366f1, #a855f7 55%, #ec4899)" }}>
          <span className="text-white font-bold text-sm">C</span>
        </div>
        {!isCollapsed && <span className="text-sm font-bold agents-title truncate">CaseMinder</span>}
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-4 scrollbar-thin" data-testid="sidebar-nav-groups">
        {NAV_GROUPS.filter((group) => typeof group.visible !== "function" || group.visible(userMeta)).map((group) => {
          const visibleItems = group.items
            .filter((item) => typeof item.visible !== "function" || item.visible(userMeta));
          // BM-H3648: a collapsible group stays open while you are on one of
          // its pages, so the highlighted link is never hidden. In the narrow
          // icon-only sidebar there is no heading to click, so it shows all.
          const groupHasActive = visibleItems.some((item) => isActive(item.path));
          const groupOpen = !group.collapsible || isCollapsed || groupHasActive || !!openGroups[group.label];
          const groupSlug = group.label.toLowerCase().replace(/\s+/g, "-");
          return (
            <div key={group.label} style={hueVars(group.label)}>
              {!isCollapsed && group.collapsible && (
                <button
                  type="button"
                  onClick={() => toggleGroup(group.label)}
                  disabled={groupHasActive}
                  aria-expanded={groupOpen}
                  data-testid={`sidebar-group-toggle-${groupSlug}`}
                  title={groupHasActive ? `${group.label} stays open while you are on one of its pages` : (groupOpen ? `Hide ${group.label}` : `Show ${group.label}`)}
                  className="cm-nav-heading w-full flex items-center gap-1 px-2 mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-300 select-none disabled:cursor-default disabled:hover:text-slate-500"
                >
                  {groupOpen ? <ChevronDown size={12} className="shrink-0" /> : <ChevronRight size={12} className="shrink-0" />}
                  <span className="truncate">{group.label}</span>
                  {!groupOpen && (
                    <span className="ml-auto normal-case tracking-normal font-normal text-slate-500">{visibleItems.length}</span>
                  )}
                </button>
              )}
              {!isCollapsed && !group.collapsible && (
                <div className="cm-nav-heading flex items-center px-2 mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 select-none">
                  {group.label}
                </div>
              )}
              {groupOpen && (
              <div className={group.showDots && !isCollapsed ? "relative ml-[11px]" : "space-y-0.5"}>
                {/* Vertical connector line for dot groups */}
                {group.showDots && !isCollapsed && (
                  <div className="absolute left-[3px] top-[14px] bottom-[14px] w-px bg-slate-700" />
                )}
                {visibleItems.map((item) => {
                  const Icon = group.showDots ? null : item.icon;
                  const active = isActive(item.path);
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      title={item.tooltip || item.label}
                      data-testid={`sidebar-link-${item.path.replace(/\//g, "-").replace(/^-/, "")}`}
                      className={`cm-nav-link flex items-center rounded-md text-[13px] transition-colors duration-150 ${
                        group.showDots ? "gap-2 relative pl-0 pr-2 py-[7px]" : "gap-2.5"
                      } ${
                        !group.showDots && (isCollapsed ? "justify-center px-2 py-2" : "px-2.5 py-[7px]")
                      } ${
                        active
                          ? "cm-nav-active text-white font-medium"
                          : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                      }`}
                    >
                      {group.showDots ? (
                        <span className={`cm-nav-dot relative z-10 w-[7px] h-[7px] rounded-full shrink-0 ${
                          active ? "" : "bg-slate-600"
                        }`} />
                      ) : (
                        Icon && <Icon size={16} className="shrink-0" />
                      )}
                      {!isCollapsed && (
                        <span className="truncate flex-1">{item.label}</span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-slate-700/50 px-2 py-2 space-y-0.5 shrink-0" style={hueVars()}>
        {FOOTER_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <NavLink
              key={item.path}
              to={item.path}
              title={isCollapsed ? item.label : undefined}
              data-testid={`sidebar-link-${item.path.replace(/\//g, "-").replace(/^-/, "")}`}
              className={`cm-nav-link flex items-center gap-2.5 rounded-md text-[13px] transition-colors duration-150 ${
                isCollapsed ? "justify-center px-2 py-2" : "px-2.5 py-[7px]"
              } ${
                active
                  ? "cm-nav-active text-white font-medium"
                  : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
              }`}
            >
              <Icon size={16} className="shrink-0" />
              {!isCollapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          );
        })}
        <button
          onClick={logout}
          data-testid="sidebar-logout-btn"
          title={isCollapsed ? "Logout" : undefined}
          className={`flex items-center gap-2.5 rounded-md text-[13px] w-full transition-colors duration-150 text-slate-400 hover:bg-status-red-dim/30 hover:text-status-red ${
            isCollapsed ? "justify-center px-2 py-2" : "px-2.5 py-[7px]"
          }`}
        >
          <LogOut size={16} className="shrink-0" />
          {!isCollapsed && <span className="truncate">Logout</span>}
        </button>
      </div>

      {/* Collapse & Pin toggles */}
      <div className="hidden lg:flex items-center border-t border-slate-700/50 shrink-0">
        {!pinned && (
          <button
            onClick={() => setCollapsed((c) => !c)}
            data-testid="sidebar-collapse-toggle"
            className="flex-1 flex items-center justify-center h-9 text-slate-500 hover:text-slate-300 hover:bg-slate-800 transition-colors duration-150"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        )}
        <button
          onClick={() => setPinned((p) => !p)}
          data-testid="sidebar-pin-toggle"
          className={`flex-1 flex items-center justify-center h-9 transition-colors duration-150 ${
            pinned
              ? "text-teal hover:text-teal/80 hover:bg-slate-800"
              : "text-slate-500 hover:text-slate-300 hover:bg-slate-800"
          }`}
          title={pinned ? "Unpin sidebar" : "Pin sidebar open"}
        >
          {pinned ? <PinOff size={16} /> : <Pin size={16} />}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <button
        onClick={() => setMobileOpen(true)}
        data-testid="sidebar-mobile-hamburger"
        className="lg:hidden fixed top-3 left-3 z-[60] p-2 rounded-md bg-slate-800/90 text-slate-300 hover:bg-slate-700 backdrop-blur-sm transition-colors"
        aria-label="Open navigation"
      >
        <Menu size={20} />
      </button>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            ref={overlayRef}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative w-64 bg-navy border-r border-slate-700/50 shadow-2xl z-10 animate-in slide-in-from-left duration-200">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-3 right-3 p-1 rounded text-slate-500 hover:text-slate-300"
              aria-label="Close navigation"
            >
              <X size={18} />
            </button>
            {sidebarContent}
          </div>
        </div>
      )}

      <aside
        data-testid="sidebar-desktop"
        className={`hidden lg:flex flex-col bg-navy border-r border-slate-700/50 h-screen shrink-0 transition-all duration-200 ease-in-out ${
          pinned || !collapsed ? "w-56" : "w-[52px]"
        }`}
      >
        {sidebarContent}
      </aside>
    </>
  );
}
